import React from "react";
import { createPortal } from "react-dom";
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import ScienceIcon from '@mui/icons-material/Science';
import LayersIcon from '@mui/icons-material/Layers';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AssessmentIcon from '@mui/icons-material/Assessment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import CancelIcon from '@mui/icons-material/Cancel';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import InboxIcon from '@mui/icons-material/Inbox';

const MoliendaSection = ({
  moliendaRecords,
  coccionRecords,
  isFormOpen,
  setIsFormOpen,
  formData,
  setFormData,
  handleSaveMolienda,
  handleCancelMolienda,
  handleRejectMolienda
}) => {
  const [selectedRecord, setSelectedRecord] = React.useState(null);

  const moliendaItems = formData?.items || [];

  const handleEditRecord = (rec) => {
    if (rec.status !== "borrador" && rec.status !== "rechazado") {
      alert("Solo se pueden editar registros que se encuentren en estado BORRADOR o RECHAZADO.");
      return;
    }
    setFormData({
      noProduccion: rec.noProduccion || "",
      observaciones: rec.observaciones || "",
      items: (rec.items && Array.isArray(rec.items)) ? rec.items : []
    });
    setIsFormOpen(true);
  };

  // Agregar una nueva fila vacía
  const handleAddItem = () => {
    const today = new Date().toISOString().split('T')[0];
    const nextTinaNum = moliendaItems.length + 1;
    const newItem = {
      id: Date.now(),
      fechaFermentacion: today,
      contenedor: `Tina ${nextTinaNum < 10 ? '0' + nextTinaNum : nextTinaNum}`,
      kgMaguey: "",
      fechaFormulacion: today,
      volumenFormulado: "",
      fechaPrimeraDestilacion: today,
      volumenMezcal: "",
      pctAlcMezcal: "",
      volumenColas: "",
      pctAlcColas: ""
    };
    setFormData(prev => ({
      ...prev,
      items: [...(prev.items || []), newItem]
    }));
  };

  const handleRemoveItem = (id) => {
    if (moliendaItems.length <= 1) {
      alert("Debe existir al menos un contenedor en el registro.");
      return;
    }
    setFormData(prev => ({
      ...prev,
      items: (prev.items || []).filter(item => item.id !== id)
    }));
  };

  const handleItemChange = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: (prev.items || []).map(item => item.id === id ? { ...item, [field]: value } : item)
    }));
  };

  // Cálculos en tiempo real de totales para el banner métrico
  const totalKgMaguey = moliendaItems.reduce((acc, item) => acc + (parseFloat(item.kgMaguey) || 0), 0);
  const totalVolFormulado = moliendaItems.reduce((acc, item) => acc + (parseFloat(item.volumenFormulado) || 0), 0);
  const totalVolMezcal = moliendaItems.reduce((acc, item) => acc + (parseFloat(item.volumenMezcal) || 0), 0);
  const totalVolColas = moliendaItems.reduce((acc, item) => acc + (parseFloat(item.volumenColas) || 0), 0);

  if (!isFormOpen) {
    return (
      <div className="animate-fadeIn">
        <div className="history-section-header">
          <div>
            <h2>Historial de Molienda, Fermentación & 1ra Destilación (Sección III)</h2>
            <p style={{ color: "var(--text-muted)", fontSize: "13px", margin: "4px 0 0 0" }}>
              Bitácora dinámica de seguimiento por contenedor y formulación
            </p>
          </div>
          <button
            onClick={() => setIsFormOpen(true)}
            className="btn-add-new"
          >
            <AddIcon style={{ fontSize: 18 }} />
            <span>Nuevo Registro de Molienda</span>
          </button>
        </div>

        {moliendaRecords.length === 0 ? (
          <div className="empty-history-card">
            <InboxIcon className="empty-history-icon" />
            <h3>No hay bitácoras de molienda registradas</h3>
            <p style={{ margin: 0, color: "var(--text-muted)", fontSize: "13px" }}>
              Actualmente no existen registros guardados. Haga clic en 'Nuevo Registro de Molienda' para agregar uno.
            </p>
          </div>
        ) : (
          <div className="table-responsive-container">
            <table className="custom-table-bitacora">
              <thead>
                <tr>
                  <th>No. Producción</th>
                  <th>Fecha Bitácora</th>
                  <th>Inspector</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {moliendaRecords.map((rec, i) => {
                  const fechaBitacora = rec.fechaFermentacion || (rec.items && rec.items[0] ? rec.items[0].fechaFermentacion : '-');

                  return (
                    <tr key={i}>
                      <td className="font-bold-primary">{rec.noProduccion}</td>
                      <td style={{ color: "var(--text-secondary)", fontSize: "12.5px" }}>{fechaBitacora}</td>
                      <td>
                        <span className="inspector-tag">{rec.inspector || 'Pendiente'}</span>
                      </td>
                      <td>
                        <span className={`badge-status ${rec.status || 'borrador'}`}>
                          {rec.status === "aprobado" 
                            ? "Aprobado" 
                            : rec.status === "enviado" 
                            ? "Enviado" 
                            : rec.status === "revision" 
                            ? "En revisión" 
                            : rec.status === "rechazado" 
                            ? "Rechazado (Para corrección)" 
                            : (rec.status === "cancelada" || rec.status === "cancelado") 
                            ? "Cancelada" 
                            : "Borrador"}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                          <button
                            onClick={() => setSelectedRecord(rec)}
                            title="Ver detalles completos"
                            className="btn-action-icon view"
                          >
                            <VisibilityIcon style={{ fontSize: 16 }} />
                          </button>
                          {(rec.status === "borrador" || rec.status === "rechazado") && (
                            <button
                              onClick={() => handleEditRecord(rec)}
                              title={rec.status === "rechazado" ? "Editar y corregir bitácora" : "Editar Borrador"}
                              className="btn-action-icon edit"
                            >
                              <EditIcon style={{ fontSize: 16 }} />
                            </button>
                          )}
                          {rec.status !== "cancelada" && rec.status !== "cancelado" && rec.status !== "aprobado" && handleRejectMolienda && (
                            <button
                              onClick={() => handleRejectMolienda(rec)}
                              title="Rechazar para Corrección"
                              className="btn-action-icon reject"
                              style={{ color: "#D97706", background: "white", borderColor: "rgba(245, 158, 11, 0.3)" }}
                            >
                              <ReportProblemIcon style={{ fontSize: 16 }} />
                            </button>
                          )}
                          {rec.status !== "cancelada" && rec.status !== "cancelado" && handleCancelMolienda && (
                            <button
                              onClick={() => handleCancelMolienda(rec)}
                              title="Cancelar Bitácora"
                              className="btn-action-icon cancel"
                            >
                              <CancelIcon style={{ fontSize: 16 }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}


        {/* MODAL DE DETALLE COMPLETO DE MOLIENDA */}
        {selectedRecord && createPortal(
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: "20px" }}>
            <div className="detail-card animate-fadeIn" style={{ maxWidth: "850px", width: "100%", maxHeight: "90vh", overflowY: "auto", background: "#161b22", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "12px", padding: "24px", position: "relative" }}>
              <button 
                onClick={() => setSelectedRecord(null)}
                style={{ position: "absolute", top: "16px", right: "16px", background: "transparent", border: "none", color: "var(--text-muted)", cursor: "pointer" }}
              >
                <CloseIcon />
              </button>

              <div style={{ borderBottom: "1px solid rgba(255,255,255,0.1)", paddingBottom: "12px", marginBottom: "16px" }}>
                <span className={`badge-status ${selectedRecord.status}`} style={{ float: "right", marginTop: "4px" }}>
                  {selectedRecord.status === "aprobado" ? "Aprobado" : selectedRecord.status === "revision" ? "En revisión" : (selectedRecord.status === "cancelada" || selectedRecord.status === "cancelado") ? "Cancelada" : "Borrador"}
                </span>
                <h3 style={{ margin: 0, color: "var(--agave-green)", fontSize: "20px" }}>Solicitud: {selectedRecord.noProduccion}</h3>
                <small style={{ color: "var(--text-muted)" }}>Detalle Completo de Molienda, Fermentación & 1ra Destilación</small>
              </div>

              {/* TABLA DE ITEMS */}
              <div className="records-list-wrapper" style={{ overflowX: "auto", marginBottom: "16px" }}>
                <table className="records-table">
                  <thead>
                    <tr>
                      <th colSpan="3" style={{ textAlign: "center", background: "rgba(46, 204, 113, 0.15)", color: "var(--agave-green)" }}>Molienda y Fermentación</th>
                      <th colSpan="2" style={{ textAlign: "center", background: "rgba(230, 126, 34, 0.15)", color: "var(--copper)" }}>Formulación</th>
                      <th colSpan="4" style={{ textAlign: "center", background: "rgba(52, 152, 219, 0.15)", color: "#3498db" }}>1ra Destilación</th>
                    </tr>
                    <tr>
                      <th>Fecha Ferm.</th>
                      <th>Contenedor</th>
                      <th>kg Maguey</th>
                      <th>Fecha Form.</th>
                      <th>Vol. Form. (L)</th>
                      <th>Fecha 1ra Dest.</th>
                      <th>Mezcal (L) / % Alc.</th>
                      <th>Colas (L) / % Alc.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedRecord.items && selectedRecord.items.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.fechaFermentacion}</td>
                        <td style={{ fontWeight: 600 }}>{item.contenedor}</td>
                        <td style={{ color: "var(--agave-green)", fontWeight: 700 }}>{item.kgMaguey}</td>
                        <td>{item.fechaFormulacion}</td>
                        <td style={{ fontWeight: 700 }}>{item.volumenFormulado}</td>
                        <td>{item.fechaPrimeraDestilacion}</td>
                        <td style={{ color: "var(--copper)", fontWeight: 700 }}>
                          {item.volumenMezcal} <br />
                          <small style={{ color: "var(--text-muted)", fontSize: "11px" }}>{item.pctAlcMezcal}</small>
                        </td>
                        <td>
                          {item.volumenColas} <br />
                          <small style={{ color: "var(--text-muted)", fontSize: "11px" }}>{item.pctAlcColas}</small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ paddingTop: "12px", borderTop: "1px solid rgba(255,255,255,0.1)" }}>
                <strong>Observaciones Generales:</strong>
                <p style={{ margin: "6px 0 0 0", color: "var(--text-muted)", fontSize: "13px" }}>{selectedRecord.observaciones || "Sin observaciones."}</p>
              </div>

              {(selectedRecord.observaciones_correccion || selectedRecord.observacionesCorreccion) && (
                <div style={{ marginTop: "12px", background: "rgba(245, 158, 11, 0.12)", border: "1px solid rgba(245, 158, 11, 0.3)", borderRadius: "10px", padding: "12px" }}>
                  <span style={{ color: "#D97706", fontSize: "12px", fontWeight: 800, display: "flex", alignItems: "center", gap: "6px" }}>
                    ⚠️ Observaciones de Corrección (Unidad de Inspección UI):
                  </span>
                  <p style={{ margin: "6px 0 0 0", color: "#F59E0B", fontSize: "13px", fontWeight: 600 }}>
                    {selectedRecord.observaciones_correccion || selectedRecord.observacionesCorreccion}
                  </p>
                </div>
              )}

              <div style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                {(selectedRecord.status === "borrador" || selectedRecord.status === "rechazado") && (
                  <button 
                    onClick={() => {
                      const rec = selectedRecord;
                      setSelectedRecord(null);
                      handleEditRecord(rec);
                    }}
                    className="btn-add-new"
                    style={{ padding: "8px 16px", fontSize: "13px" }}
                  >
                    <EditIcon style={{ fontSize: 16 }} />
                    <span>Editar Registro</span>
                  </button>
                )}
                {selectedRecord.status !== "cancelada" && selectedRecord.status !== "cancelado" && selectedRecord.status !== "aprobado" && handleRejectMolienda && (
                  <button 
                    onClick={() => {
                      const rec = selectedRecord;
                      setSelectedRecord(null);
                      handleRejectMolienda(rec);
                    }}
                    style={{ background: "rgba(245, 158, 11, 0.15)", color: "#D97706", border: "1px solid rgba(245, 158, 11, 0.3)", borderRadius: "6px", padding: "8px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 700 }}
                  >
                    <ReportProblemIcon style={{ fontSize: 16 }} />
                    <span>Rechazar</span>
                  </button>
                )}
                {selectedRecord.status !== "cancelada" && selectedRecord.status !== "cancelado" && handleCancelMolienda && (
                  <button 
                    onClick={() => {
                      const rec = selectedRecord;
                      setSelectedRecord(null);
                      handleCancelMolienda(rec);
                    }}
                    style={{ background: "rgba(239, 68, 68, 0.15)", color: "#ef4444", border: "1px solid rgba(239, 68, 68, 0.3)", borderRadius: "6px", padding: "8px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 600 }}
                  >
                    <CancelIcon style={{ fontSize: 16 }} />
                    <span>Cancelar</span>
                  </button>
                )}
                <button onClick={() => setSelectedRecord(null)} className="btn-secondary-custom" style={{ padding: "8px 16px" }}>
                  Cerrar
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      </div>
    );
  }

  return (
    <div className="animate-fadeIn">
      {/* ENCABEZADO Y VOLVER */}
      <div className="history-section-header" style={{ marginBottom: "16px" }}>
        <button onClick={() => setIsFormOpen(false)} className="btn-secondary-custom" style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 16px" }}>
          <ArrowBackIcon style={{ fontSize: 16 }} />
          <span>Volver al Historial</span>
        </button>
        <h2>Captura Rápida - Sección III (Molienda & 1ra Destilación)</h2>
      </div>

      {/* SELECCIÓN DE LOTE + BANNER DE TOTALES EN TIEMPO REAL */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "16px", marginBottom: "20px" }}>

        {/* Selector de Lote */}
        <section className="detail-card detail-card-accent-left-green" style={{ margin: 0, padding: "16px" }}>
          <div className="custom-input-group" style={{ marginTop: 0 }}>
            <select
              value={formData.noProduccion || ""}
              onChange={e => setFormData(prev => ({ ...prev, noProduccion: e.target.value }))}
              className="custom-select-field"
              style={{ padding: "16px 14px 6px 14px" }}
            >
              <option value="">-- Seleccionar No. de Producción --</option>
              {coccionRecords.map((r, idx) => (
                <option key={idx} value={r.noProduccion}>
                  {r.noProduccion} ({r.tipoMaguey})
                </option>
              ))}
            </select>
            <label className="custom-input-label">No. de Producción (Lote Validado)</label>
          </div>
        </section>

        {/* Resumen Métrico de Totales en Tiempo Real */}
        <section className="detail-card" style={{ margin: 0, padding: "16px", display: "flex", alignItems: "center", justifyContent: "space-around" }}>
          <div style={{ textAlign: "center" }}>
            <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 700 }}>Total Maguey</span>
            <h4 style={{ margin: "4px 0 0 0", color: "var(--agave-green)", fontSize: "18px", fontWeight: 800 }}>{totalKgMaguey.toLocaleString()} <small style={{ fontSize: "12px" }}>kg</small></h4>
          </div>
          <div style={{ width: "1px", height: "30px" }}></div>
          <div style={{ textAlign: "center" }}>
            <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 700 }}>Formulado</span>
            <h4 style={{ margin: "4px 0 0 0", color: "var(--copper)", fontSize: "18px", fontWeight: 800 }}>{totalVolFormulado.toLocaleString()} <small style={{ fontSize: "12px" }}>L</small></h4>
          </div>
          <div style={{ width: "1px", height: "30px" }}></div>
          <div style={{ textAlign: "center" }}>
            <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 700 }}>Mezcal (1ra)</span>
            <h4 style={{ margin: "4px 0 0 0", color: "#3498db", fontSize: "18px", fontWeight: 800 }}>{totalVolMezcal.toLocaleString()} <small style={{ fontSize: "12px" }}>L</small></h4>
          </div>
        </section>
      </div>

      {/* TARJETAS COMPACTAS POR CONTENEDOR DE CAPTURA RÁPIDA (DISEÑO HORIZONTAL DE 3 PASOS) */}
      {moliendaItems.map((item, index) => (
        <div
          key={item.id}
          className="detail-card animate-fadeIn"
          style={{
            marginBottom: "16px",
            padding: "16px",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            borderRadius: "12px"
          }}
        >
          {/* Header de la Tina / Fila con Acciones de Duplicado Rápido */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px", paddingBottom: "8px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ background: "rgba(46, 204, 113, 0.15)", color: "var(--agave-green)", fontWeight: 800, padding: "4px 12px", borderRadius: "6px", fontSize: "13px" }}>
                Proceso #{index + 1} {item.contenedor ? `• ${item.contenedor}` : ""}
              </span>
            </div>

            <div style={{ display: "flex", gap: "8px" }}>
              {moliendaItems.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveItem(item.id)}
                  title="Eliminar este contenedor"
                  style={{ background: "rgba(231, 76, 60, 0.15)", color: "#e74c3c", border: "1px solid rgba(231, 76, 60, 0.3)", borderRadius: "6px", padding: "4px 8px", cursor: "pointer", display: "flex", alignItems: "center", fontSize: "12px" }}
                >
                  <DeleteOutlineIcon style={{ fontSize: 16 }} />
                </button>
              )}
            </div>
          </div>

          {/* GRID DE 3 COLUMNAS COMPACTAS SIDE-BY-SIDE */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "16px" }}>

            {/* 1. Molienda y Fermentación */}
            <div style={{ background: "rgba(46, 204, 113, 0.03)", padding: "12px 14px", borderRadius: "8px", border: "1px solid rgba(46, 204, 113, 0.15)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "10px", color: "var(--agave-green)", fontWeight: 700, fontSize: "13px" }}>
                <WaterDropIcon style={{ fontSize: 16 }} />
                <span>1. Molienda & Fermentación</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                <div className="custom-input-group" style={{ margin: 0 }}>
                  <input
                    type="date"
                    value={item.fechaFermentacion}
                    onChange={e => handleItemChange(item.id, 'fechaFermentacion', e.target.value)}
                    className="custom-input-field"
                    style={{ padding: "14px 10px 4px 10px", fontSize: "13px" }}
                  />
                  <label className="custom-input-label" style={{ fontSize: "11px" }}>Fecha Fermentación</label>
                </div>
                <div className="custom-input-group" style={{ margin: 0 }}>
                  <input
                    type="text"
                    placeholder="Ej: Tina 01"
                    value={item.contenedor}
                    onChange={e => handleItemChange(item.id, 'contenedor', e.target.value)}
                    className="custom-input-field"
                    style={{ padding: "14px 10px 4px 10px", fontSize: "13px" }}
                  />
                  <label className="custom-input-label" style={{ fontSize: "11px" }}>Contenedor / Tina</label>
                </div>
                <div className="custom-input-group" style={{ margin: 0, gridColumn: "span 2" }}>
                  <input
                    type="number"
                    placeholder="8500"
                    value={item.kgMaguey}
                    onChange={e => handleItemChange(item.id, 'kgMaguey', e.target.value)}
                    className="custom-input-field"
                    style={{ padding: "14px 10px 4px 10px", fontSize: "13px" }}
                  />
                  <label className="custom-input-label" style={{ fontSize: "11px" }}>kg Maguey</label>
                  <span className="input-suffix" style={{ fontSize: "11px" }}>kg</span>
                </div>
              </div>
            </div>

            {/* 2. Formulación */}
            <div style={{ background: "rgba(230, 126, 34, 0.03)", padding: "12px 14px", borderRadius: "8px", border: "1px solid rgba(230, 126, 34, 0.15)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "10px", color: "var(--copper)", fontWeight: 700, fontSize: "13px" }}>
                <ScienceIcon style={{ fontSize: 16 }} />
                <span>2. Formulación</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                <div className="custom-input-group" style={{ margin: 0 }}>
                  <input
                    type="date"
                    value={item.fechaFormulacion}
                    onChange={e => handleItemChange(item.id, 'fechaFormulacion', e.target.value)}
                    className="custom-input-field"
                    style={{ padding: "14px 10px 4px 10px", fontSize: "13px" }}
                  />
                  <label className="custom-input-label" style={{ fontSize: "11px" }}>Fecha Formulación</label>
                </div>
                <div className="custom-input-group" style={{ margin: 0 }}>
                  <input
                    type="number"
                    placeholder="3200"
                    value={item.volumenFormulado}
                    onChange={e => handleItemChange(item.id, 'volumenFormulado', e.target.value)}
                    className="custom-input-field"
                    style={{ padding: "14px 10px 4px 10px", fontSize: "13px" }}
                  />
                  <label className="custom-input-label" style={{ fontSize: "11px" }}>Vol. Formulado</label>
                  <span className="input-suffix" style={{ fontSize: "11px" }}>L</span>
                </div>
              </div>
            </div>

            {/* 3. Primera Destilación */}
            <div style={{ background: "rgba(52, 152, 219, 0.03)", padding: "12px 14px", borderRadius: "8px", border: "1px solid rgba(52, 152, 219, 0.15)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "10px", color: "#3498db", fontWeight: 700, fontSize: "13px" }}>
                <LayersIcon style={{ fontSize: 16 }} />
                <span>3. 1ra Destilación</span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "8px" }}>
                <div className="custom-input-group" style={{ margin: 0, gridColumn: "span 2" }}>
                  <input
                    type="date"
                    value={item.fechaPrimeraDestilacion}
                    onChange={e => handleItemChange(item.id, 'fechaPrimeraDestilacion', e.target.value)}
                    className="custom-input-field"
                    style={{ padding: "14px 10px 4px 10px", fontSize: "13px" }}
                  />
                  <label className="custom-input-label" style={{ fontSize: "11px" }}>Fecha 1ra Destilación</label>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                <div className="custom-input-group" style={{ margin: 0 }}>
                  <input
                    type="number"
                    placeholder="1250"
                    value={item.volumenMezcal}
                    onChange={e => handleItemChange(item.id, 'volumenMezcal', e.target.value)}
                    className="custom-input-field"
                    style={{ padding: "14px 10px 4px 10px", fontSize: "13px" }}
                  />
                  <label className="custom-input-label" style={{ fontSize: "11px" }}>Vol Mezcal (L)</label>
                </div>
                <div className="custom-input-group" style={{ margin: 0 }}>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="26.5"
                    value={item.pctAlcMezcal}
                    onChange={e => handleItemChange(item.id, 'pctAlcMezcal', e.target.value)}
                    className="custom-input-field"
                    style={{ padding: "14px 10px 4px 10px", fontSize: "13px" }}
                  />
                  <label className="custom-input-label" style={{ fontSize: "11px" }}>% Alc. Vol.</label>
                </div>

                <div className="custom-input-group" style={{ margin: 0 }}>
                  <input
                    type="number"
                    placeholder="180"
                    value={item.volumenColas}
                    onChange={e => handleItemChange(item.id, 'volumenColas', e.target.value)}
                    className="custom-input-field"
                    style={{ padding: "14px 10px 4px 10px", fontSize: "13px" }}
                  />
                  <label className="custom-input-label" style={{ fontSize: "11px" }}>Vol Colas (L)</label>
                </div>
                <div className="custom-input-group" style={{ margin: 0 }}>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="15.0"
                    value={item.pctAlcColas}
                    onChange={e => handleItemChange(item.id, 'pctAlcColas', e.target.value)}
                    className="custom-input-field"
                    style={{ padding: "14px 10px 4px 10px", fontSize: "13px" }}
                  />
                  <label className="custom-input-label" style={{ fontSize: "11px" }}>% Alc. Colas</label>
                </div>
              </div>
            </div>

          </div>
        </div>
      ))}

      {/* BOTÓN INFERIOR DE AGREGAR RÁPIDO */}
      <div style={{ display: "flex", justifyContent: "center", margin: "16px 0" }}>
        <button
          type="button"
          onClick={handleAddItem}
          style={{
            background: "rgba(46, 204, 113, 0.1)",
            color: "var(--agave-green)",
            border: "1px dashed var(--agave-green)",
            borderRadius: "8px",
            padding: "10px 24px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontWeight: 700,
            fontSize: "14px"
          }}
        >
          <AddIcon style={{ fontSize: 18 }} />
          <span>+ Agregar Otro Contenedor / Tina</span>
        </button>
      </div>

      {/* OBSERVACIONES GENERALES POR SOLICITUD */}
      <section className="detail-card detail-card-accent-left-copper" style={{ marginTop: "16px", padding: "16px" }}>
        <header className="detail-card-header" style={{ marginBottom: "12px" }}>
          <div className="detail-card-icon-wrapper-copper">
            <SettingsIcon style={{ fontSize: 20 }} />
          </div>
          <div className="detail-card-title-wrapper">
            <h2 style={{ fontSize: "15px" }}>Observaciones Generales de la Solicitud</h2>
            <p style={{ fontSize: "12px" }}>Notas o contingencias aplicables a todo el lote</p>
          </div>
        </header>

        <div className="custom-input-group" style={{ margin: 0 }}>
          <textarea
            id="moliendaObservaciones"
            value={formData.observaciones || ""}
            onChange={e => setFormData(prev => ({ ...prev, observaciones: e.target.value }))}
            className="custom-input-field"
            style={{ height: "80px", resize: "none", padding: "12px", fontSize: "13px" }}
            placeholder="Observaciones generales de la solicitud..."
          />
          <label className="custom-input-label" htmlFor="moliendaObservaciones">Observaciones Generales</label>
        </div>
      </section>

      {/* BOTONES DE ACCIÓN PRINCIPALES */}
      <div className="button-row" style={{ marginTop: "20px" }}>
        <button type="button" className="btn-secondary-custom" onClick={() => handleSaveMolienda("borrador")}>Guardar Borrador</button>
        <button type="button" className="btn-primary-custom" onClick={() => handleSaveMolienda("revision")}>
          <span>Registrar Sección III ({moliendaItems.length} Tinas)</span>
          <ArrowForwardIcon style={{ fontSize: 16 }} />
        </button>
      </div>
    </div>
  );
};

export default MoliendaSection;
